import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Code,
  Api,
  Assistant,
  AutoAwesome,
  Analytics,
  Extension,
  Link as LinkIcon,
  Home,
  SmartToy,
  CheckCircle,
  ArrowForward,
  GitHub,
  ContentCopy,
  Email,
  Hub,
  Dashboard,
  People,
  DeviceHub,
  Settings,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

function DeveloperContent() {
  const theme = useTheme();

  const consoleFeatures = [
    {
      icon: Extension,
      title: "Capability Integrations",
      description:
        "Configure schemas for your integrations and capabilities. The platform will handle the rest to make it work on our Apps and APIs.",
    },
    {
      icon: AutoAwesome,
      title: "Automation Suggestions",
      description:
        "Create your predefined automation suggestions to promote device features.",
    },
    {
      icon: Analytics,
      title: "Usage Analytics",
      description: "Monitor of usages, and other metrics.",
    },
    {
      icon: People,
      title: "Community Friendly",
      description:
        "Console is available for individual users too. Configure your own integrations, and connect your own devices to mHome.",
    },
  ];

  const apiFeatures = [
    {
      icon: Assistant,
      title: "AI Agent",
      description:
        "Use mHome's AI Agent to fulfill user requests in your services.",
    },
    {
      icon: CheckCircle,
      title: "Broader Data Access",
      description:
        "Access mHome connected integrations and devices (upon user consent and integration provider's policy).",
    },
  ];

  const mcpFeatures = [
    {
      icon: DeviceHub,
      title: "mHome as MCP Server",
      description:
        "Provide automation as a common feature for other AI agents.",
    },
    {
      icon: Hub,
      title: "mHome as MCP client",
      description:
        "Leverage other MCP servers in our platform, enhance your automations with more capabilities.",
    },
  ];

  // Sections data structure
  const sections = [
    {
      id: "console",
      title: "Developer Console",
      subtitle: "Web-based dashboard for managing your integrations and more.",
      chipLabel: "Coming Soon",
      chipStyle: "primary",
      features: consoleFeatures,
      leftContent: true,
    },
    {
      id: "apis",
      title: "APIs",
      subtitle: "Use our APIs to build your own solutions.",
      chipLabel: "Coming Soon",
      chipStyle: "primary",
      features: apiFeatures,
      leftContent: false,
    },
    {
      id: "mcps",
      title: "MCP & A2A",
      subtitle: "Work with other AI agents.",
      chipLabel: "Later This Year",
      chipStyle: "secondary",
      features: mcpFeatures,
      leftContent: true,
    },
  ];

  const renderFeatureList = (features) => (
    <List sx={{ p: 0 }}>
      {features.map((feature, index) => {
        const IconComponent = feature.icon;
        return (
          <ListItem key={index} sx={{ px: 0, py: 1 }}>
            <ListItemIcon>
              <IconComponent sx={{ color: theme.palette.text.primary }} />
            </ListItemIcon>
            <ListItemText
              primary={feature.title}
              secondary={feature.description}
              primaryTypographyProps={{
                color: theme.palette.text.primary,
                fontWeight: 500,
              }}
              secondaryTypographyProps={{
                color: theme.palette.text.secondary,
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );

  const renderSectionTitle = (title, chipLabel, chipStyle) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: "2rem", md: "2.5rem" },
          fontWeight: "bold",
          color: theme.palette.text.primary,
          lineHeight: 1.2,
        }}
      >
        <Box
          component="span"
          sx={{
            background: theme.palette.brand.gradientText,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            WebkitTextFillColor: "transparent",
          }}
        >
          {title}
        </Box>
      </Typography>
      <Chip
        label={chipLabel}
        sx={{
          backgroundColor: theme.palette.chip[chipStyle].background,
          color: theme.palette.chip[chipStyle].color,
          border: `1px solid ${theme.palette.chip[chipStyle].border}`,
          fontWeight: 600,
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.gradient,
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 4,
              lineHeight: 1.1,
            }}
          >
            Build with{" "}
            <Box
              component="span"
              sx={{
                background: theme.palette.brand.gradientText,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              mhome.ai
            </Box>
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 6,
            }}
          >
            Powerful APIs, SDKs, and tools to integrate AI-driven home
            automation into your applications and services.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              mt: 4,
            }}
          >
            <Card
              sx={{
                background: theme.palette.background.glass,
                backdropFilter: "blur(10px)",
                border: `1px solid ${theme.palette.border.main}`,
                borderRadius: 2,
                maxWidth: "600px",
                width: "100%",
                boxShadow: theme.shadows[4],
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  Developer resources are coming soon! Contact us for your needs
                  at{" "}
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                    }}
                  >
                    yw@mhome.ai
                    <Tooltip title="Copy email address">
                      <IconButton
                        size="small"
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "20",
                          },
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText("yw@mhome.ai");
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Dynamic Sections */}
      {sections.map((section, index) => (
        <Container key={section.id} maxWidth="lg" sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            {section.leftContent ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderFeatureList(section.features)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSectionTitle(
                    section.title,
                    section.chipLabel,
                    section.chipStyle,
                  )}
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6,
                      mb: 4,
                    }}
                  >
                    {section.subtitle}
                  </Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  {renderSectionTitle(
                    section.title,
                    section.chipLabel,
                    section.chipStyle,
                  )}
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6,
                      mb: 4,
                    }}
                  >
                    {section.subtitle}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderFeatureList(section.features)}
                </Grid>
              </>
            )}
          </Grid>
        </Container>
      ))}
    </Box>
  );
}

export default DeveloperContent;
